import axios from 'axios';

import { API_BASE_URL } from '../config';
import { auth } from '../firebase';

export const contactsApi = {
    // Fetch jobs available to the authenticated client for manual journey creation.
    listJobs: async (limit = 100) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/api/job_descriptions`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                },
                params: { limit }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching jobs:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Fetch contacts (paginated)
    getContacts: async (params = {}) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/api/contacts`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                },
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching contacts:', error);
            throw error;
        }
    },

    // Fetch contact transcripts
    getContactTranscripts: async (contactId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/get_call_transcript/${contactId}`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching transcripts:', error);
            throw error;
        }
    },

    // Fetch contact CV
    getContactCV: async (contactId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/contacts/${contactId}/cv`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching CV:', error);
            throw error;
        }
    },

    // Fetch contact stats
    getContactStats: async (contactId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const encodedContactId = encodeURIComponent(contactId);
            const response = await axios.get(`${API_BASE_URL}/contacts/${encodedContactId}/stats`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching stats:', error);
            // Return a formatted error response
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to fetch contact stats'
            };
        }
    },

    /**
     * Full email/call/sms/whatsapp history for a candidate on a specific autonomous campaign.
     * Uses campaign membership + resume client_id (see services/agent/routes.py).
     */
    getCampaignCandidateCommunication: async (goalId, resumeId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const encoded = encodeURIComponent(resumeId);
            const response = await axios.get(
                `${API_BASE_URL}/api/campaigns/autonomous/${goalId}/candidates/${encoded}/communication`,
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching campaign candidate communication:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to load communication'
            };
        }
    },

    // Fetch WhatsApp conversation
    getWhatsAppConversation: async (contactId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const encodedContactId = encodeURIComponent(contactId);
            const response = await axios.get(`${API_BASE_URL}/contacts/${encodedContactId}/whatsapp`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching WhatsApp conversation:', error);
            // Return a formatted error response
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to fetch WhatsApp conversation'
            };
        }
    },

    // Send CV email
    sendCV: async (payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(`${API_BASE_URL}/api/email/send-cv`, payload, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error sending CV:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Generate cover email (subject/body) using LLM
    generateCVCover: async (payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(`${API_BASE_URL}/api/email/generate-cv-cover`, payload, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error generating CV cover:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Format CV into branded PDF (Playwright HTML-to-PDF or ReportLab backend)
    formatCV: async (payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(`${API_BASE_URL}/api/cv/format`, payload, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error formatting CV:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Format a structured candidate intelligence profile into HTML/PDF
    formatCandidateProfile: async (payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(`${API_BASE_URL}/api/candidate-profile/format`, payload, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error formatting candidate profile:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Upsert placement journey for a candidate/job pair
    upsertPlacementJourney: async (payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(`${API_BASE_URL}/api/candidate-placement-journeys/upsert`, payload, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error upserting placement journey:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // List all placement journeys for a candidate
    listPlacementJourneys: async (candidateId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/api/candidate-placement-journeys/candidate/${encodeURIComponent(candidateId)}`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error listing placement journeys:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Fetch a placement journey for a specific candidate/job pair
    getPlacementJourney: async (candidateId, jobId) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(
                `${API_BASE_URL}/api/candidate-placement-journeys/${encodeURIComponent(candidateId)}/${encodeURIComponent(jobId)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching placement journey:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Update the stage of an existing placement journey
    updatePlacementJourneyStage: async (journeyId, payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.patch(
                `${API_BASE_URL}/api/candidate-placement-journeys/${encodeURIComponent(journeyId)}/stage`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating placement journey stage:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    },

    // Append a timeline event to a placement journey
    addPlacementJourneyEvent: async (journeyId, payload) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            const idToken = await user.getIdToken();
            const response = await axios.post(
                `${API_BASE_URL}/api/candidate-placement-journeys/${encodeURIComponent(journeyId)}/events`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error adding placement journey event:', error);
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }
};
